import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import delv from 'dlv';
import { isEqual } from 'lodash-es';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { DeliveryPriceDisclaimer } from 'components/delivery-price-disclaimer';
import FavoritesCardList from 'components/favorites-card-list';
import { useFeatureMenuPage } from 'components/features/menu-page/use-feature-menu-page';
import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import MenuOrderWizard from 'components/menu-order-wizard';
import MenuTileGrid from 'components/menu-tile-grid';
import MenuTileStack from 'components/menu-tile-stack';
import { MenuTileUnavailable } from 'components/menu-tile-unavailable';
import RecentOrderCardList from 'components/recent-order-card-list';
import StoreSelected from 'components/store-selected';
import { TabbedHeader } from 'components/tabbed-header';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import useMenuByDayPart from 'hooks/use-menu-by-day-part';
import { useTabbedMenuHeader } from 'hooks/use-tabbed-menu-header';
import { SHOULD_CHECK_ITEM_IN_OFFER } from 'pages/main-menu/constants';
import { ProductWizard } from 'pages/product-wizard';
import analyticsProvider from 'state/analytics';
import { itemViewedEvent } from 'state/analytics/events';
import { selectors, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { isLegacyOffer, isLoyaltyOffer } from 'state/loyalty/types';
import { useNewMainMenuContext } from 'state/main-menu';
import { useMenuContext } from 'state/menu';
import { SectionUiPattern } from 'state/menu/types';
import { useOffersContext } from 'state/offers';
import { useScrollContext } from 'state/scroll';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { primitive } from 'styles/constants/primitives';
import { getType } from 'utils/menu';
import { isFavoritesRoute, isRecentItemsRoute, routes } from 'utils/routing';

import { filterWithChannelExclusions } from '../main-menu/utils';

import MenuItemUnavailable from './item-unavailable';
import MainContainer from './main-container';
import MainMenuHeader from './main-menu-header';
import { MainMenuLink } from './main-menu-link';
import MainMenuSectionHeader from './main-menu-section-header';
import { menuSectionShowsItemPrices } from './menu-section-shows-item-prices';
import PickerBackgroundImage from './picker-background-image';

const InnerContentContainer = styled.div`
  & > :last-child {
    padding-bottom: ${primitive.$spacing3};
  }
`;

const VisuallyHiddenHeading = styled.h1`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  white-space: nowrap;
  word-wrap: normal;
`;

const RenderWizard = memo(({ menuObject }) => <MenuOrderWizard {...menuObject} />);
const RenderStacked = memo(({ sections }) =>
  sections.map(section => (
    <MenuTileStack
      key={section._id}
      options={section.options}
      name={section.name?.locale}
      image={section.image}
      description={section.description}
    />
  ))
);

const RenderInlineTileGrid = memo(({ sections }) => {
  return sections.map(section => (
    <MenuTileGrid
      key={section._id}
      data={section.options || []}
      gridTitle={delv(section, 'name.locale')}
      objectFitContain={true}
    />
  ));
});

const RenderStandard = memo(({ sections }) =>
  sections.length > 0 ? <MenuTileGrid data={sections} objectFitContain /> : null
);

const isPickerItemOrComboOnMenuObject = menuObject =>
  menuObject && ['picker', 'item', 'combo'].some(type => type === getType(menuObject.data));

const InnerContent = ({
  menuObject,
  pickerBackgroundImage,
  shouldUseTabbedMenuBar,
  path,
  isOffer,
  isStaticMenu,
  serviceMode,
}) => {
  const { scrollTo } = useScrollContext();
  useEffect(() => {
    scrollTo({ y: 0, animated: false });
  }, [scrollTo]);
  const enableNewMenuCustomizationDesign = useFlag(
    LaunchDarklyFlag.ENABLE_NEW_MENU_CUSTOMIZATION_DESIGN
  );

  const filteredOptions = (menuObject.data?.options ?? []).filter(option => {
    // Filter menu options based on channel exclusions
    const filteredWithChannelExclusions = filterWithChannelExclusions(
      option.channelExclusions,
      serviceMode
    );
    // Filter menu options based on static menu option
    const filteredWithStaticMenu = isStaticMenu ? option.showInStaticMenu : true;
    return filteredWithChannelExclusions && filteredWithStaticMenu;
  });

  const stackedSections = useMemo(() => {
    if (!Array.isArray(filteredOptions)) {
      return [];
    }
    return filteredOptions.filter(
      option => option && option.uiPattern === SectionUiPattern.STACKED
    );
  }, [filteredOptions]);

  const inlineTileSections = useMemo(() => {
    if (!Array.isArray(filteredOptions)) {
      return [];
    }
    return filteredOptions.filter(
      option => option && option.uiPattern === SectionUiPattern.INLINE_TILE
    );
  }, [filteredOptions]);

  const standardSections = useMemo(() => {
    if (!Array.isArray(filteredOptions)) {
      return [];
    }
    return filteredOptions.filter(
      option =>
        option &&
        option.uiPattern !== SectionUiPattern.STACKED &&
        option.uiPattern !== SectionUiPattern.INLINE_TILE
    );
  }, [filteredOptions]);

  if (!menuObject.data) {
    if (isRecentItemsRoute(path)) {
      return <RecentOrderCardList />;
    }
    if (isFavoritesRoute(path)) {
      return <FavoritesCardList />;
    }
    return <MenuItemUnavailable isOffer={isOffer} />;
  }

  if (isPickerItemOrComboOnMenuObject(menuObject)) {
    // will eventually decouple from menu content and use layout to route to its page
    if (enableNewMenuCustomizationDesign) {
      return <ProductWizard product={menuObject.data} />;
    }

    return (
      <>
        <MainMenuLink backToFullMenu={shouldUseTabbedMenuBar} type={getType(menuObject.data)} />
        <PickerBackgroundImage image={pickerBackgroundImage} />
        <RenderWizard menuObject={menuObject} />
      </>
    );
  }

  if (!filteredOptions?.length) {
    return <MenuTileUnavailable />;
  }

  const sectionName = menuObject.data?.name?.locale;

  return (
    <InnerContentContainer>
      {sectionName && <VisuallyHiddenHeading>{sectionName}</VisuallyHiddenHeading>}
      <MainMenuLink backToFullMenu={shouldUseTabbedMenuBar} type={getType(menuObject.data)} />
      <RenderStandard sections={standardSections} />
      <RenderStacked sections={stackedSections} />
      <RenderInlineTileGrid sections={inlineTileSections} />
    </InnerContentContainer>
  );
};

function curriedTryGetMenuObject(getMenuObject, setMenuItemNotFound, logger) {
  return id => {
    try {
      return getMenuObject(id);
    } catch (err) {
      if (err.notFoundError) {
        setMenuItemNotFound(true);

        logger.warn({ error: err, menuObjectId: id });

        return {};
      }

      // not sure we should throw here...
      throw err;
    }
  };
}

// Placing these util hooks here since they are used only in this file but in both new and legacy content components
const useSelectedOffer = () => {
  const { selectedOffer: selectedLegacyOffer } = useOffersContext();
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);

  return selectedLoyaltyOffer || selectedLegacyOffer;
};

const useHandleMenuItemInOffer = setIsOffer => {
  const navigate = useNavigate();
  const selectedOffer = useSelectedOffer();

  return menuItem => {
    const isOfferBenefit = menuItem?.isOfferBenefit;
    setIsOffer(isOfferBenefit);

    const isSelectedOfferBenefit = () => {
      let result = false;
      const menuItemId = menuItem._id;

      if (isLoyaltyOffer(selectedOffer)) {
        result = selectedOffer?.incentives?.some(incentive => incentive._id === menuItemId);
      } else if (isLegacyOffer(selectedOffer)) {
        result = selectedOffer?.option?._id === menuItemId;
      }

      return result;
    };

    // This will prevent the case of a user going back to the product wizard for an offer item
    // without having the offer context causing price to show 0 and the item not adding to cart
    if (isOfferBenefit && (!selectedOffer || (selectedOffer && !isSelectedOfferBenefit()))) {
      navigate(routes.offers);
    }
  };
};

const NewMenuContent = () => {
  const { formatMessage } = useIntl();
  const logger = useLogger();
  const { id } = useParams();
  const { region } = useLocale();
  const {
    loading,
    data: menuData,
    getMainMenu,
    getMenuObject,
    isOffer,
    setIsOffer,
    isStaticMenu,
  } = useNewMainMenuContext();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const isDektop = useMediaQuery(MediaQuery.Desktop);
  const theme = useTheme();
  const { menuId, featureMenuLoading } = useFeatureMenuPage();
  const { store } = useStoreContext();
  const storeId = store.number || '';
  const handleMenuItemInOffer = useHandleMenuItemInOffer(setIsOffer);
  const shouldUseTabbedMenuBar = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR);

  const [menuItemNotFound, setMenuItemNotFound] = useState(false);
  // Trying to leave this intact causes cascading re-renders
  const memoizedMenu = useMemo(() => getMainMenu(), [getMainMenu]);
  const mainMenu = menuData?.Menu || memoizedMenu;
  const loggedView = useRef({});

  const isLoading = featureMenuLoading || loading;

  useEffect(() => {
    async function mainMenu() {
      if (!menuData && !loading) {
        await getMainMenu(menuId, region, storeId);
      }
    }

    mainMenu();
  }, [getMainMenu, loading, menuData, menuId, region, storeId]);

  const {
    activeTab,
    isRouteOnItemPickerAndOutsideTabbedItems,
    isTabbedMenuLoading,
    tabbedHeaderItems,
  } = useTabbedMenuHeader({});

  const tryGetMenuObject = useMemo(
    () => curriedTryGetMenuObject(getMenuObject, setMenuItemNotFound, logger),
    [getMenuObject, logger]
  );

  // if its recent items or favorites don't get menu data
  const getMenuObjectData =
    isRecentItemsRoute(location.pathname) || isFavoritesRoute(location.pathname)
      ? { loading: false, data: null }
      : tryGetMenuObject(id);

  const menuObject = useMenuByDayPart(getMenuObjectData);
  const documentTitle = delv(menuObject, 'data.name.locale', '');

  useEffect(() => {
    if (!isLoading && !isEqual(loggedView.current, menuObject.data) && !menuItemNotFound) {
      if (!menuObject.data) {
        return;
      }

      const { data: menuObjectData } = menuObject;

      if (SHOULD_CHECK_ITEM_IN_OFFER) {
        handleMenuItemInOffer(menuObjectData);
      }

      const sections = {
        name: menuObjectData.name?.locale,
        menuType: menuObjectData?._type,
        id: menuObjectData?._id,
      };

      const isMenuPickerOrItemPage = /^\/(menu)\/([item|picker].*)$/.test(location.pathname);
      if (isMenuPickerOrItemPage) {
        analyticsProvider.logEvent(
          itemViewedEvent({ menuData: sections, path: location.pathname })
        );
      }

      loggedView.current = menuObject.data;
    }
  }, [
    menuItemNotFound,
    menuObject.data,
    menuObject.loading,
    location,
    menuObject,
    loading,
    menuData,
    isLoading,
    handleMenuItemInOffer,
  ]);

  if (menuItemNotFound) {
    navigate(`${routes.notFound}/404`, { replace: true });
  }

  const shouldRenderMainMenuSectionHeader =
    menuObject._type === 'section' && (isDektop || !shouldUseTabbedMenuBar);

  const shouldRenderTabbedHeader =
    shouldUseTabbedMenuBar && !isRouteOnItemPickerAndOutsideTabbedItems && isMobile;

  if (isLoading) {
    return (
      <LoadingContainer
        ref={node => {
          node?.focus();
        }}
        tabIndex={0}
        aria-label={formatMessage({ id: 'loading' })}
      >
        <LoadingAnimation fillColor={theme.token('icon-active')} />
      </LoadingContainer>
    );
  }

  const isItemAvailable = !isLoading && menuObject.data?._id;

  return (
    <StoreSelected>
      <Helmet title={documentTitle} />
      {isItemAvailable && <MainMenuHeader />}
      <MainContainer data-testid="menu-content" $addMainMenuHeaderHeight={!isItemAvailable}>
        {shouldRenderMainMenuSectionHeader && <MainMenuSectionHeader section={menuObject.data} />}
        {shouldRenderTabbedHeader && (
          <TabbedHeader
            data={tabbedHeaderItems}
            loading={isLoading || isTabbedMenuLoading}
            activeTab={activeTab}
          />
        )}
        {menuSectionShowsItemPrices(menuObject.data) && (
          <DeliveryPriceDisclaimer placement="menu" />
        )}
        <InnerContent
          menuObject={menuObject}
          pickerBackgroundImage={delv(mainMenu, 'pickerBackgroundImage')}
          path={location.pathname}
          shouldUseTabbedMenuBar={shouldUseTabbedMenuBar}
          isOffer={isOffer}
          isStaticMenu={isStaticMenu}
        />
      </MainContainer>
    </StoreSelected>
  );
};

const LegacyMenuContent = () => {
  const { formatMessage } = useIntl();
  const logger = useLogger();
  const { getMenuObject, getMainMenu, isOffer, setIsOffer, isStaticMenu } = useMenuContext();

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const isDektop = useMediaQuery(MediaQuery.Desktop);
  const { serviceMode } = useServiceModeContext();
  const theme = useTheme();
  const handleMenuItemInOffer = useHandleMenuItemInOffer(setIsOffer);
  const shouldUseTabbedMenuBar = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR);

  const [menuItemNotFound, setMenuItemNotFound] = useState(false);
  const tryGetMenuObject = useMemo(
    () => curriedTryGetMenuObject(getMenuObject, setMenuItemNotFound, logger),
    [getMenuObject, logger]
  );

  // if its recent items or favorites don't get menu data
  const menuObjectData =
    isRecentItemsRoute(location.pathname) || isFavoritesRoute(location.pathname)
      ? { loading: false, data: null }
      : tryGetMenuObject(id);

  const menuObject = useMenuByDayPart(menuObjectData);
  const mainMenu = useMemo(() => getMainMenu(), [getMainMenu]);
  const loggedView = useRef({});
  const documentTitle = delv(menuObject, 'data.name.locale', '');

  useEffect(() => {
    if (!menuObject.loading && !isEqual(loggedView.current, menuObject.data) && !menuItemNotFound) {
      const { data: menuData } = menuObject;
      if (!menuData) {
        return;
      }
      if (SHOULD_CHECK_ITEM_IN_OFFER) {
        handleMenuItemInOffer(menuData);
      }

      const sections = {
        name: menuData.name?.locale,
        menuType: menuData?._type,
        id: menuData?._id,
      };

      const isMenuPickerOrItemPage = /^\/(menu)\/([item|picker].*)$/.test(location.pathname);
      if (isMenuPickerOrItemPage) {
        analyticsProvider.logEvent(
          itemViewedEvent({ menuData: sections, path: location.pathname })
        );
      }

      loggedView.current = menuObject.data;
    }
  }, [
    menuItemNotFound,
    menuObject.data,
    menuObject.loading,
    location,
    menuObject,
    handleMenuItemInOffer,
  ]);

  if (menuItemNotFound) {
    navigate(`${routes.notFound}/404`, { replace: true });
  }

  const {
    activeTab,
    isRouteOnItemPickerAndOutsideTabbedItems,
    isTabbedMenuLoading,
    tabbedHeaderItems,
  } = useTabbedMenuHeader({});

  const shouldRenderMainMenuSectionHeader =
    menuObject.data?._type === 'section' && (isDektop || !shouldUseTabbedMenuBar);

  const shouldRenderTabbedHeader =
    shouldUseTabbedMenuBar && !isRouteOnItemPickerAndOutsideTabbedItems && isMobile;

  const isItemAvailable = !menuObject.loading && menuObject.data;

  return (
    <StoreSelected>
      <Helmet title={documentTitle} />
      {isItemAvailable && <MainMenuHeader />}
      <MainContainer data-testid="menu-content">
        {shouldRenderMainMenuSectionHeader && <MainMenuSectionHeader section={menuObject.data} />}
        {shouldRenderTabbedHeader && (
          <TabbedHeader
            data={tabbedHeaderItems}
            loading={mainMenu.loading || isTabbedMenuLoading}
            activeTab={activeTab}
          />
        )}
        {menuSectionShowsItemPrices(menuObject.data) && (
          <DeliveryPriceDisclaimer placement="menu" />
        )}
        {menuObject.loading ? (
          <LoadingContainer
            ref={node => {
              node?.focus();
            }}
            tabIndex={0}
            aria-label={formatMessage({ id: 'loading' })}
          >
            <LoadingAnimation fillColor={theme.token('icon-active')} />
          </LoadingContainer>
        ) : (
          <InnerContent
            menuObject={menuObject}
            pickerBackgroundImage={delv(mainMenu.data, 'pickerBackgroundImage')}
            path={location.pathname}
            shouldUseTabbedMenuBar={shouldUseTabbedMenuBar}
            isOffer={isOffer}
            isStaticMenu={isStaticMenu}
            serviceMode={serviceMode}
          />
        )}
      </MainContainer>
    </StoreSelected>
  );
};

const MenuContent = () => {
  const enableMenuServiceData = useFlag(LaunchDarklyFlag.ENABLE_MENU_SERVICE_DATA);
  const location = useLocation();

  if (enableMenuServiceData && /section-/.test(location.pathname)) {
    return <NewMenuContent />;
  }

  return <LegacyMenuContent />;
};

export default MenuContent;
